import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { PlatformUtil } from 'src/app/shared/utils/platform/platform.util';

/**
 * localStorage key holding the one-time token a legitimate Open Banking flow
 * must present when arriving at /open-banking/success or /open-banking/error.
 *
 * Set by in-progress.component right before redirecting the user to the
 * Experian widget. Consumed (removed) the first time the guard accepts it, so
 * a later typed URL cannot ride on a stale token.
 *
 * Stored value shape: JSON string of { value: string, expiresAt: number }.
 */
export const OB_RETURN_TOKEN_KEY = 'ob-return-token';

/**
 * Query-string parameter the return token is passed through when Experian
 * redirects the user back to our success / error pages. The guard matches the
 * value of this param against the localStorage entry above.
 */
export const OB_RETURN_TOKEN_PARAM = 't';

/** Tokens expire 30 minutes after issue - matches Experian's session TTL. */
export const OB_RETURN_TOKEN_TTL_MS = 30 * 60 * 1000;

interface StoredReturnToken {
  value: string;
  expiresAt: number;
}

/**
 * Guards /open-banking/success and /open-banking/error against direct URL
 * navigation. Without the guard, a user typing either URL triggers ngOnInit
 * which marks the Open Banking ActionLog as Complete - giving the adviser a
 * false green without any bank data connected.
 *
 * Allowed arrival paths:
 *   1. Native platform (Capacitor) - URL typing isn't a practical attack.
 *   2. Router navigation state includes obSkipStatusUpdate or message -
 *      internal Angular navigation from our own code (cross-tab forward or
 *      in-progress error redirect).
 *   3. URL carries a one-time token (?t=...) that matches the entry in
 *      localStorage written by in-progress.component - covers the cross-tab
 *      return from Experian.
 *
 * Otherwise redirects to /dashboard.
 */
export const openBankingReturnGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): boolean | UrlTree => {
  const router = inject(Router);

  if (PlatformUtil.isNative()) {
    return true;
  }

  if (hasInternalNavigationState(router)) {
    consumeReturnToken();
    return true;
  }

  if (consumeReturnTokenIfMatches(route)) {
    return true;
  }

  return router.parseUrl('/dashboard');
};

/**
 * Issues a fresh one-time return token. Stores it in localStorage and returns
 * the token value so the caller can append it to the Experian returnUrl.
 */
export function issueOpenBankingReturnToken(): string {
  const value = generateToken();
  const entry: StoredReturnToken = {
    value,
    expiresAt: Date.now() + OB_RETURN_TOKEN_TTL_MS,
  };
  try {
    localStorage.setItem(OB_RETURN_TOKEN_KEY, JSON.stringify(entry));
  } catch {
    // localStorage unavailable. The guard's internal-state path still
    // covers same-tab scenarios, so the attack block is not weakened.
  }
  return value;
}

function hasInternalNavigationState(router: Router): boolean {
  const navigation = router.getCurrentNavigation();
  const state = (navigation?.extras?.state ??
    (window.history.state as Record<string, unknown> | undefined)) as
    | Record<string, unknown>
    | undefined;

  if (!state) return false;
  return state['obSkipStatusUpdate'] === true || typeof state['message'] === 'string';
}

function consumeReturnTokenIfMatches(route: ActivatedRouteSnapshot): boolean {
  const incoming = route.queryParamMap.get(OB_RETURN_TOKEN_PARAM);
  if (!incoming) return false;

  const stored = readStoredToken();
  if (!stored) return false;

  if (stored.value !== incoming) return false;
  if (stored.expiresAt < Date.now()) {
    consumeReturnToken();
    return false;
  }

  consumeReturnToken();
  return true;
}

function readStoredToken(): StoredReturnToken | null {
  try {
    const raw = localStorage.getItem(OB_RETURN_TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredReturnToken;
    if (typeof parsed?.value !== 'string' || typeof parsed?.expiresAt !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function consumeReturnToken(): void {
  try {
    localStorage.removeItem(OB_RETURN_TOKEN_KEY);
  } catch {
    // localStorage unavailable - nothing to consume.
  }
}

function generateToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
